/**
 * Create A/B Test Modal
 * Story: E007-S011 - A/B Test Manager
 * Acceptance Criteria 1: Create A/B Test
 */

import React, { useState, useEffect } from 'react';
import { abTestsApi } from '../../lib/abTestsApi';
import type { CreateTestRequest, VariantConfig, TestTemplate } from '../../types/ab-tests';
import { GOAL_METRIC_LABELS, CONFIDENCE_LEVEL_OPTIONS, TRAFFIC_SPLIT_OPTIONS } from '../../types/ab-tests';

interface CreateABTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateABTestModal: React.FC<CreateABTestModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TestTemplate[]>([]);
  
  // Form state
  const [formData, setFormData] = useState<CreateTestRequest>({
    name: '',
    goal_metric: 'ctr',
    variants: [
      { variant_name: 'Control', traffic_percentage: 50 },
      { variant_name: 'Variant B', traffic_percentage: 50 }
    ],
    confidence_level: 0.95,
    traffic_split_method: 'equal',
    duration_days: null
  });
  
  // Sample size calculator state
  const [sampleCalc, setSampleCalc] = useState({
    baseline_value: 0.02, // 2% baseline CTR
    minimum_detectable_effect: 20, // 20% lift
    show: false
  });

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      const data = await abTestsApi.getTemplates();
      setTemplates(data.templates);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await abTestsApi.create(formData);
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create test');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({
      name: '',
      goal_metric: 'ctr',
      variants: [
        { variant_name: 'Control', traffic_percentage: 50 },
        { variant_name: 'Variant B', traffic_percentage: 50 }
      ],
      confidence_level: 0.95,
      traffic_split_method: 'equal',
      duration_days: null
    });
    setError(null);
    setSampleCalc({ baseline_value: 0.02, minimum_detectable_effect: 20, show: false });
    onClose();
  };

  const addVariant = () => {
    const newVariants = [...formData.variants];
    const equalSplit = 100 / (newVariants.length + 1);
    
    // Recalculate traffic percentages for equal split
    newVariants.forEach(v => v.traffic_percentage = equalSplit);
    newVariants.push({
      variant_name: `Variant ${String.fromCharCode(65 + newVariants.length)}`,
      traffic_percentage: equalSplit
    });
    
    setFormData({ ...formData, variants: newVariants });
  };

  const removeVariant = (index: number) => {
    if (formData.variants.length <= 2) return; // Keep minimum 2 variants
    
    const newVariants = formData.variants.filter((_, i) => i !== index);
    const equalSplit = 100 / newVariants.length;
    newVariants.forEach(v => v.traffic_percentage = equalSplit);
    
    setFormData({ ...formData, variants: newVariants });
  };

  const updateVariant = (index: number, field: keyof VariantConfig, value: any) => {
    const newVariants = [...formData.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setFormData({ ...formData, variants: newVariants });
  };

  const applyTemplate = (template: TestTemplate) => {
    setFormData({
      ...formData,
      goal_metric: template.goal_metric,
      confidence_level: template.confidence_level,
      duration_days: template.recommended_duration_days
    });
    setSampleCalc({
      ...sampleCalc,
      minimum_detectable_effect: 20,
      show: false
    });
  };

  const calculateSampleSize = () => {
    const result = abTestsApi.calculateSampleSize({
      baseline_value: sampleCalc.baseline_value,
      minimum_detectable_effect: sampleCalc.minimum_detectable_effect,
      confidence_level: formData.confidence_level || 0.95,
      statistical_power: 0.80
    });
    return result;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-boxdark rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stroke dark:border-strokedark">
          <h2 className="text-2xl font-bold text-black dark:text-white">
            Create A/B Test
          </h2>
          <button
            onClick={handleClose}
            className="text-bodydark hover:text-black dark:hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center space-x-4 p-4 border-b border-stroke dark:border-strokedark">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= s ? 'bg-primary text-white' : 'bg-stroke dark:bg-strokedark text-bodydark'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-1 mx-2 ${
                step > s ? 'bg-primary' : 'bg-stroke dark:bg-strokedark'
              }`} />}
            </div>
          ))}
        </div>

        {error && (
          <div className="mx-6 mt-4 p-4 bg-danger bg-opacity-10 border border-danger rounded-lg">
            <p className="text-danger text-sm">{error}</p>
          </div>
        )}

        {/* Step Content */}
        <div className="p-6">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
                  Test Configuration
                </h3>
                
                {/* Test Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-black dark:text-white mb-2">
                    Test Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Header Variation Test"
                    className="w-full px-4 py-2 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white"
                  />
                </div>

                {/* Goal Metric */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-black dark:text-white mb-2">
                    Goal Metric *
                  </label>
                  <select
                    value={formData.goal_metric}
                    onChange={(e) => setFormData({ ...formData, goal_metric: e.target.value as any })}
                    className="w-full px-4 py-2 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white"
                  >
                    {Object.entries(GOAL_METRIC_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                {/* Confidence Level */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-black dark:text-white mb-2">
                    Confidence Level
                  </label>
                  <select
                    value={formData.confidence_level}
                    onChange={(e) => setFormData({ ...formData, confidence_level: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white"
                  >
                    {CONFIDENCE_LEVEL_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Duration */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-black dark:text-white mb-2">
                    Duration (Days)
                  </label>
                  <input
                    type="number"
                    value={formData.duration_days || ''}
                    onChange={(e) => setFormData({ ...formData, duration_days: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder="Auto (recommended)"
                    className="w-full px-4 py-2 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white"
                    min="1"
                    max="90"
                  />
                  <p className="text-xs text-bodydark mt-1">
                    Leave empty for auto-determination based on statistical significance
                  </p>
                </div>
              </div>

              {/* Templates */}
              <div>
                <h4 className="text-sm font-semibold text-black dark:text-white mb-3">
                  Quick Start Templates
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {templates.slice(0, 4).map(template => (
                    <button
                      key={template.id}
                      onClick={() => applyTemplate(template)}
                      className="p-3 border border-stroke dark:border-strokedark rounded-lg hover:border-primary hover:bg-primary hover:bg-opacity-5 text-left transition"
                    >
                      <div className="font-medium text-sm text-black dark:text-white">{template.name}</div>
                      <div className="text-xs text-bodydark mt-1">{template.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Variants */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-black dark:text-white">
                  Test Variants ({formData.variants.length})
                </h3>
                <button
                  onClick={addVariant}
                  disabled={formData.variants.length >= 10}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  + Add Variant
                </button>
              </div>

              {formData.variants.map((variant, index) => (
                <div key={index} className="p-4 border border-stroke dark:border-strokedark rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={variant.variant_name}
                        onChange={(e) => updateVariant(index, 'variant_name', e.target.value)}
                        className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white font-medium"
                        placeholder="Variant name"
                      />
                      {index === 0 && (
                        <span className="text-xs text-bodydark mt-1 block">Control variant</span>
                      )}
                    </div>
                    {formData.variants.length > 2 && (
                      <button
                        onClick={() => removeVariant(index)}
                        className="ml-2 text-danger hover:text-opacity-80"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-bodydark mb-1">Creative ID (optional)</label>
                      <input
                        type="text"
                        value={variant.creative_id || ''}
                        onChange={(e) => updateVariant(index, 'creative_id', e.target.value || undefined)}
                        placeholder="Leave empty for manual"
                        className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-bodydark mb-1">Traffic %</label>
                      <input
                        type="number"
                        value={variant.traffic_percentage}
                        onChange={(e) => updateVariant(index, 'traffic_percentage', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white text-sm"
                        min="0"
                        max="100"
                        step="1"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Traffic Split Method */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-black dark:text-white mb-2">
                  Traffic Split Method
                </label>
                <div className="space-y-2">
                  {TRAFFIC_SPLIT_OPTIONS.map(opt => (
                    <label key={opt.value} className="flex items-start p-3 border border-stroke dark:border-strokedark rounded-lg cursor-pointer hover:border-primary">
                      <input
                        type="radio"
                        value={opt.value}
                        checked={formData.traffic_split_method === opt.value}
                        onChange={(e) => setFormData({ ...formData, traffic_split_method: e.target.value as any })}
                        className="mt-1 mr-3"
                      />
                      <div>
                        <div className="font-medium text-black dark:text-white">{opt.label}</div>
                        <div className="text-xs text-bodydark">{opt.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review & Sample Size */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
                  Review & Launch
                </h3>

                <div className="space-y-3 p-4 bg-gray-50 dark:bg-meta-4 rounded-lg">
                  <div className="flex justify-between">
                    <span className="text-bodydark">Test Name:</span>
                    <span className="font-medium text-black dark:text-white">{formData.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-bodydark">Goal Metric:</span>
                    <span className="font-medium text-black dark:text-white">{GOAL_METRIC_LABELS[formData.goal_metric]}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-bodydark">Variants:</span>
                    <span className="font-medium text-black dark:text-white">{formData.variants.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-bodydark">Confidence Level:</span>
                    <span className="font-medium text-black dark:text-white">{(formData.confidence_level! * 100)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-bodydark">Duration:</span>
                    <span className="font-medium text-black dark:text-white">
                      {formData.duration_days ? `${formData.duration_days} days` : 'Auto'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sample Size Calculator */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-black dark:text-white">
                    Sample Size Calculator (Optional)
                  </h4>
                  <button
                    onClick={() => setSampleCalc({ ...sampleCalc, show: !sampleCalc.show })}
                    className="text-primary text-sm hover:underline"
                  >
                    {sampleCalc.show ? 'Hide' : 'Show'}
                  </button>
                </div>

                {sampleCalc.show && (
                  <div className="p-4 border border-stroke dark:border-strokedark rounded-lg space-y-3">
                    <div>
                      <label className="block text-xs text-bodydark mb-1">Baseline {formData.goal_metric.toUpperCase()}</label>
                      <input
                        type="number"
                        value={sampleCalc.baseline_value}
                        onChange={(e) => setSampleCalc({ ...sampleCalc, baseline_value: parseFloat(e.target.value) })}
                        step="0.001"
                        className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-bodydark mb-1">Minimum Detectable Effect (%)</label>
                      <input
                        type="number"
                        value={sampleCalc.minimum_detectable_effect}
                        onChange={(e) => setSampleCalc({ ...sampleCalc, minimum_detectable_effect: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white text-sm"
                      />
                    </div>
                    
                    {(() => {
                      const calc = calculateSampleSize();
                      return (
                        <div className="mt-4 p-3 bg-primary bg-opacity-10 rounded-lg">
                          <div className="text-sm font-medium text-black dark:text-white mb-2">
                            Required: {calc.required_sample_size_per_variant.toLocaleString()} impressions/variant
                          </div>
                          <div className="text-xs text-bodydark">
                            Estimated Duration: ~{calc.estimated_duration_days} days
                          </div>
                          {calc.warnings.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {calc.warnings.map((w, i) => (
                                <div key={i} className="text-xs text-warning">{w}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-stroke dark:border-strokedark">
          <button
            onClick={step === 1 ? handleClose : () => setStep(step - 1)}
            className="px-4 py-2 border border-stroke dark:border-strokedark rounded-lg text-black dark:text-white hover:bg-gray-50 dark:hover:bg-meta-4"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && (!formData.name || !formData.goal_metric)}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-success text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Test'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
